import type { ReactNode } from 'react';
import { cx } from './cx';

export type Tone = 'neutral' | 'brand' | 'success' | 'warn' | 'danger' | 'info';

export interface BadgeProps {
  tone?: Tone;
  /** Leading square. Use when the stamp stands for a status rather than a label. */
  dot?: boolean;
  /** Filled reads louder. Reserve it for the one status that needs to shout. */
  solid?: boolean;
  size?: 'sm' | 'md';
  className?: string;
  /** Announce the stamp's meaning when the visible text alone is cryptic. */
  title?: string;
  children: ReactNode;
}

/*
 * The stamp. A rectangle with a 1px border, JetBrains Mono 600 at 12px, uppercase,
 * 0.04em tracking, 3px radius, on a faint fill. Tone is carried by border and
 * text. It should look like it was stamped onto the row, not floated over it.
 *
 * This is the only place a status colour is decided. Success is the brand
 * green, not a second green.
 */
const tones: Record<Tone, { soft: string; solid: string; dot: string }> = {
  neutral: {
    soft: 'bg-paper-deep text-ink-2 border-line-strong',
    solid: 'bg-ink text-paper border-ink',
    dot: 'bg-ink-3',
  },
  brand: {
    soft: 'bg-forest-whisper text-forest border-forest',
    solid: 'bg-forest text-[rgb(var(--ct-on-brand-rgb))] border-forest',
    dot: 'bg-forest',
  },
  success: {
    soft: 'bg-success-soft text-success border-success',
    solid: 'bg-success text-[rgb(var(--ct-on-brand-rgb))] border-success',
    dot: 'bg-success',
  },
  warn: {
    soft: 'bg-warn-soft text-warn border-warn',
    solid: 'bg-warn text-[rgb(var(--ct-on-brand-rgb))] border-warn',
    dot: 'bg-warn',
  },
  danger: {
    soft: 'bg-danger-soft text-danger border-danger',
    solid: 'bg-danger text-[rgb(var(--ct-on-brand-rgb))] border-danger',
    dot: 'bg-danger',
  },
  info: {
    soft: 'bg-info-soft text-info border-info',
    solid: 'bg-info text-[rgb(var(--ct-on-brand-rgb))] border-info',
    dot: 'bg-info',
  },
};

/** The stamp's fixed recipe, exported so a table cell can stamp without the component. */
export const stampBase =
  'inline-flex items-center gap-1.5 rounded-ct-xs border font-mono font-semibold uppercase tracking-[0.04em] whitespace-nowrap leading-4';

export function Badge({ tone = 'neutral', dot, solid, size = 'md', className, title, children }: BadgeProps) {
  const t = tones[tone];
  return (
    <span
      title={title}
      className={cx(
        stampBase,
        // Both sizes are 12px: the floor is the floor. sm is tighter on the sides.
        size === 'sm' ? 'px-1.5 py-0 text-2xs' : 'px-2 py-0.5 text-2xs',
        solid ? t.solid : t.soft,
        className,
      )}
    >
      {dot ? <span aria-hidden="true" className={cx('h-1.5 w-1.5 shrink-0', solid ? 'bg-current' : t.dot)} /> : null}
      {children}
    </span>
  );
}
